import React, { useState, useImperativeHandle } from 'react';
import IndicatorStages from '../../../ReportBuilder/Components/ImpactSummaryTable/IndicatorStages';
import { FeedbackQuestionHandler, FeedbackSectionInfo } from '../utils';
import { FeedbackQuestionsById } from '../api';
import { parseIndicatorSummary } from '../../../ReportBuilder/Components/SectionPeriod/utils';
import { isReportDatesPassedWithQuestions } from '../../../ReportBuilder/Components/IndicatorDetails/utils';
import { useAllServiceCollections } from 'shared/apollo/pageHooks/useAllServiceCollections';
import { ServiceGroup } from '../../../ReportBuilder/Components/ImpactIndicator/utils';
import { useAllIndicatorsMin } from 'shared/apollo/pageHooks/useAllIndicators';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  CAPACITY_DEVELOPMENT_QUESTION_ID,
  IMPACT_SUMMARY_QUESTION_ID,
  INTERMEDIARIES_QUESTION_ID,
  PROGRAM_OUTCOME_QUESTION_ID,
  REACH_QUESTION_ID,
  SERVICE_GOAL_QUESTION_ID,
  SERVICE_TYPE_QUESTION_ID,
  SOCIAL_IMPACT_QUESTION_ID,
} from '../../../ReportBuilder/Components/SectionMultiRadio';
import { DATES_QUESTION_ID_1 } from '../../../ReportBuilder/Components/ReportFinalize/answersUtil';
import { INDICATOR_DETAILS_QUESTION_ID } from '../../utils';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';

const capacityDevelopmentRegEx = /^Capacity development/i;
const ImpactSummary = React.forwardRef<
  FeedbackQuestionHandler,
  FeedbackSectionInfo
>(
  (
    {
      reportId,
      questionId,
      programName,
      question,
      questions,
      questionText,
      answer,
      reloadAnswers,
      setSelectedQuestion,
    },
    forwardRef,
  ) => {
    const { data: indicatorsData } = useAllIndicatorsMin();
    const { data: serviceCollections, loading } = useAllServiceCollections({
      reportId,
    });
    const [state, setState] = useState<string | string[]>(answer);

    const allServices =
      ((serviceCollections as unknown) as ServiceGroup[])?.filter(
        coll => coll.name === questions[40002]?.answers?.[0]?.text,
      )?.[0]?.services || [];
    const service = allServices?.filter(
      ser => ser.name === questions[40003]?.answers?.[0]?.text,
    )?.[0];

    useImperativeHandle(forwardRef, () => ({
      reportId,
      questionId,
      answer: () =>
        state
          ? typeof state === 'string'
            ? state
            : JSON.stringify(state)
          : '',
      errorText: () =>
        state
          ? undefined
          : 'Please complete the impact model builder with all indicator definitions.',
    }));

    if (loading || allServices.length === 0) {
      return <TrueImpactLoading />;
    }

    if (!service) {
      return (
        <TrueImpactText color="critical" align="center">
          Unable to find service selected in earlier question.
        </TrueImpactText>
      );
    }

    const isReportPassed = isReportDatesPassedWithQuestions(questions);
    const oldValues = parseIndicatorSummary(
      questions[IMPACT_SUMMARY_QUESTION_ID]?.text,
    );
    const learnEnabled = questions[40007]?.answers[0]?.text === 'Yes';
    const actEnabled = questions[40008]?.answers[0]?.text === 'Yes';

    const reportDates = questions[DATES_QUESTION_ID_1]?.answers[0]?.text;
    const programOutcome =
      questions[PROGRAM_OUTCOME_QUESTION_ID]?.answers[0]?.text || '';
    const serviceGoal = questions[SERVICE_GOAL_QUESTION_ID]?.answers[0]?.text;
    const intermediaries =
      questions[INTERMEDIARIES_QUESTION_ID]?.answers[0]?.text;
    const serviceType = questions[SERVICE_TYPE_QUESTION_ID]?.answers[0]?.text;
    const selectedCapacityIndicators = parseIndicatorSummary(
      questions[CAPACITY_DEVELOPMENT_QUESTION_ID]?.answers[0]?.text,
    );
    const selectedReachIndicators = parseIndicatorSummary(
      questions[REACH_QUESTION_ID]?.answers[0]?.text,
    );
    const selectedSocialImpactIndicators = parseIndicatorSummary(
      questions[SOCIAL_IMPACT_QUESTION_ID]?.answers[0]?.text,
    );

    return (
      <IndicatorStages
        handleNext={(_?: boolean, forcedValue?: string | string[]) => {
          setSelectedQuestion(questions[INDICATOR_DETAILS_QUESTION_ID]);
        }}
        handleBack={() => null}
        questionId={questionId}
        title={questionText}
        beneficiaryTerm={questions[40004]?.text}
        programName={programName}
        intermediaries={intermediaries}
        serviceGoal={serviceGoal}
        serviceType={serviceType}
        reportDates={reportDates}
        reportPeriod={{
          card: 'customYear',
          startDate: undefined,
          endDate: undefined,
        }}
        helpText=""
        reloadAnswers={() => null}
        handleChange={setState}
        reportId={reportId}
        service={service}
        services={allServices}
        selectedCapacityIndicators={selectedCapacityIndicators}
        selectedReachIndicators={selectedReachIndicators}
        indicators={indicatorsData}
        capacityDevelopmentEnabled={capacityDevelopmentRegEx.test(
          programOutcome,
        )}
        learnEnabled={learnEnabled}
        actEnabled={actEnabled}
        isReportPassed={isReportPassed}
        guidanceText={undefined}
        prevQuestion={40010}
        selectedSocialImpactIndicators={selectedSocialImpactIndicators}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pageSectionProps={{} as any}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pageOptionProps={{} as any}
        indicatorSummary={oldValues}
        userAnswerId={question.answers[0]?.dbId || undefined}
        isImpactSummaryCompleted={Boolean(questions[40015])}
        choiceIsSelected={false}
      />
    );
  },
);

export default ImpactSummary;
